import React from 'react';
import { 
  Users, Crown, Shield, Ban, FileArchive, Activity, TrendingUp, DollarSign, HardDrive
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatStorageSize = (bytes) => {
  if (!bytes || bytes <= 0) return '0 o';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
};

const AdminOverviewTab = ({ stats }) => {
  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* Main Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-800/50 border border-purple-500/20 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-500/20 rounded-lg"><Users className="w-6 h-6 text-purple-400" /></div>
            <div><p className="text-3xl font-bold text-white">{stats.total_users}</p><p className="text-sm text-purple-300/60">Utilisateurs</p></div>
          </div>
        </div>
        <div className="bg-slate-800/50 border border-yellow-500/20 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-yellow-500/20 rounded-lg"><Crown className="w-6 h-6 text-yellow-400" /></div>
            <div><p className="text-3xl font-bold text-white">{stats.pro_users}</p><p className="text-sm text-yellow-300/60">Abonnés Pro</p></div>
          </div>
        </div>
        <div className="bg-slate-800/50 border border-red-500/20 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-500/20 rounded-lg"><Shield className="w-6 h-6 text-red-400" /></div>
            <div><p className="text-3xl font-bold text-white">{stats.admin_users || 1}</p><p className="text-sm text-red-300/60">Administrateurs</p></div>
          </div>
        </div>
        <div className="bg-slate-800/50 border border-amber-500/20 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-500/20 rounded-lg"><Ban className="w-6 h-6 text-amber-400" /></div>
            <div><p className="text-3xl font-bold text-white">{stats.suspended_users || 0}</p><p className="text-sm text-amber-300/60">Suspendus</p></div>
          </div>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500/20 rounded-lg"><FileArchive className="w-6 h-6 text-blue-400" /></div>
            <div><p className="text-3xl font-bold text-white">{stats.total_shares}</p><p className="text-sm text-blue-300/60">Partages totaux</p></div>
          </div>
        </div>
        <div className="bg-slate-800/50 border border-green-500/20 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-500/20 rounded-lg"><Activity className="w-6 h-6 text-green-400" /></div>
            <div><p className="text-3xl font-bold text-white">{stats.active_shares}</p><p className="text-sm text-green-300/60">Partages actifs</p></div>
          </div>
        </div>
        <div className="bg-slate-800/50 border border-cyan-500/20 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-cyan-500/20 rounded-lg"><TrendingUp className="w-6 h-6 text-cyan-400" /></div>
            <div><p className="text-3xl font-bold text-white">{stats.total_downloads}</p><p className="text-sm text-cyan-300/60">Téléchargements</p></div>
          </div>
        </div>
        <div className="bg-slate-800/50 border border-emerald-500/20 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-500/20 rounded-lg"><DollarSign className="w-6 h-6 text-emerald-400" /></div>
            <div><p className="text-3xl font-bold text-white">{stats.total_revenue}€</p><p className="text-sm text-emerald-300/60">Revenus totaux</p></div>
          </div>
        </div>
      </div>

      {/* Storage Usage Card */}
      <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-6 mt-6" data-testid="storage-usage-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-purple-500/20 rounded-lg"><HardDrive className="w-6 h-6 text-purple-400" /></div>
          <div>
            <h3 className="text-lg font-semibold text-white">Espace de stockage utilisé</h3>
            <p className="text-2xl font-bold text-purple-300" data-testid="total-storage-size">{formatStorageSize(stats.total_storage || 0)}</p>
          </div>
        </div>
        {stats.storage_per_user && stats.storage_per_user.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-blue-300/60 mb-3">Répartition par utilisateur (Top 10)</h4>
            <div className="space-y-2">
              {stats.storage_per_user.map((u, i) => {
                const pct = stats.total_storage > 0 ? (u.total_size / stats.total_storage) * 100 : 0;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-white truncate">{u.name || u.email}</span>
                        <span className="text-xs text-blue-300/60 ml-2 whitespace-nowrap">
                          {formatStorageSize(u.total_size)} ({u.file_count} fichier{u.file_count > 1 ? 's' : ''})
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOverviewTab;
